import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Icon,
  useTheme,
} from "@mui/material";
import moment from "moment";
import FetchHook from "../../Api/FetchHooks";
import PageLoader from "../../Components/PageLoader";

// Import all the icons
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import PersonAddDisabledOutlinedIcon from "@mui/icons-material/PersonAddDisabledOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import CreditScoreOutlinedIcon from "@mui/icons-material/CreditScoreOutlined";
import AddCardOutlinedIcon from "@mui/icons-material/AddCardOutlined";
import AdminStatCard from "./Components/AdminStatCard";
import Page from "../../Components/Page";
import { useNavigate } from "react-router-dom";
import AdminCharts from "./Components/AdminCharts";
import DateRangePicker from "../../Components/DateRangePicker";
import AdminLoanCharts from "./Components/AdminLoanCharts";
import { useSelector } from "react-redux";

// --- CARD CONFIGURATION ---
// ... (rest of the code)

const cardConfig = {
  total_members: {
    title: "All Members",
    icon: PeopleAltOutlinedIcon,
    color: "success",
    path: "/users/customers",
  },
  fully_registered: {
    title: "Fully Registered",
    icon: PersonOutlineOutlinedIcon,
    color: "primary",
    path: "/users/customers?status=fully_registered",
  },
  pending_members: {
    title: "Pending Members",
    icon: PersonAddAltOutlinedIcon,
    color: "info",
    path: "/users/customers?status=pending",
  },
  blocked_members: {
    title: "Blocked Members",
    icon: PersonOffOutlinedIcon,
    color: "warning",
    path: "/users/customers?status=blocked",
  },
  exit_members: {
    title: "Total Member Exits",
    icon: PersonRemoveOutlinedIcon,
    color: "error",
    path: "/users/customers?status=exited",
  },
  suspended_members: {
    title: "Suspended Members",
    icon: PersonAddDisabledOutlinedIcon,
    color: "secondary",
    path: "/users/customers?status=suspended",
  },

  loan_taken: {
    title: "Total Loans Taken",
    icon: CreditScoreOutlinedIcon,
    color: "primary",
    path: "/loans",
  },
  loan_repaid: {
    title: "Total Loans Repaid",
    icon: AddCardOutlinedIcon,
    color: "success",
    path: "/loans?status=repaid",
  },
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const loggedInUser = useSelector((state) => state.auth.user);

  const [startDate, setStartDate] = useState(new Date("2025-01-01"));
  const [endDate, setEndDate] = useState(new Date());
  const [dashReport, setDashReport] = useState([]);
  const [loans, setLoans] = useState([]); // Re-added loans state

  const [loading, response, request] = FetchHook();
  const [loanLoading, loanResponse, loanRequest] = FetchHook(); // Separate FetchHook for loans

  useEffect(() => {
    handleSearch();
    fetchLoans(); // Call fetchLoans on initial load
  }, []);

  const handleSearch = async () => {
    const start = moment(startDate).format("YYYY-MM-DD");
    const end = moment(endDate).format("YYYY-MM-DD");
    const resp = await request({
      url: `/dashreport?start_date='${start}'&end_date='${end}'`,
    });

    if (resp && resp.data.Dashboard) {
      setDashReport(resp.data.Dashboard[0]);
    }
  };

  const fetchLoans = useCallback(async () => {
    await loanRequest({ url: `loan` }); // Use loanRequest for loan API
  }, []); // Dependency array can be empty if loans are not date-dependent

  useEffect(() => {
    // if (response && response?.data.Dashboar)
    //   setDashReport(response.data.Dashboard); // Use response
    if (loanResponse && loanResponse?.data?.Loan)
      setLoans(loanResponse.data.Loan); // Use loanResponse
  }, [loanResponse]);

  const chartData = response?.data?.Dashboard?.map((item) => ({
    name: cardConfig[item.label]?.title,
    count: item.count,
  })).filter((item) => item.name);

  return (
    <Page title={"Dashboard"}>
      {/* <Card>
        <CardContent> */}

      {loading ? (
        <PageLoader />
      ) : response?.data?.Dashboard ? (
        <Grid container spacing={3}>
          {/* Stat Cards */}
          <Grid item xs={12} md={12} sx={{ mb: 2 }}>
            {/* <Card>
              <CardContent> */}
            <Box sx={{ my: 2 }}>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                onSearch={handleSearch}
                loading={loading}
              />
            </Box>

            <Grid container spacing={2}>
              {Object.entries(dashReport).length > 0 &&
                Object.entries(dashReport).map(([key, value]) => {
                  const config = cardConfig[key];
                  if (!config) return null;

                  const totalMembers =
                    response.data.Dashboard[0].total_members || 1;

                  return (
                    <Grid
                      item
                      key={key}
                      xs={12}
                      sm={6}
                      md={6}
                      lg={3}
                      sx={{ mb: 1 }}
                    >
                      <AdminStatCard
                        title={config.title}
                        count={value}
                        icon={config.icon}
                        color={config.color}
                        percentage={
                          (Number(value) / Number(totalMembers)) * 100
                        }
                        // onClick={() => navigate(config.path)}
                      />
                    </Grid>
                  );
                })}
            </Grid>
            {/* </CardContent>
            </Card> */}
          </Grid>

          {/* Chart */}
          {/* <Grid item xs={12} md={12}>
                <AdminCharts chartData={chartData} />
              </Grid> */}

          {/* AdminLoanCharts */}
          {loggedInUser?.role === "admin" ||
          loggedInUser?.role === "finance" ? (
            loanLoading ? (
              <Grid item xs={12}>
                <PageLoader />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <AdminLoanCharts loans={loans} />
              </Grid>
            )
          ) : null}
        </Grid>
      ) : (
        <Box
          sx={{
            // textAlign: "center",
            mt: 10,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Icon sx={{ fontSize: 80, color: "grey.400" }}>
            <CancelOutlinedIcon fontSize="inherit" />
          </Icon>
          <Typography variant="h6" color="text.secondary">
            {response?.response?.data?.StatusMessage || "Could not load data."}
          </Typography>
        </Box>
      )}
      {/* </CardContent>
      </Card> */}
    </Page>
  );
};

export default AdminDashboard;
